import React from 'react';

export type SplitContentTilesImage = {
    src?: string;
    url?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
    title?: string;
    originalAlt?: string;
    thumbnailAlt?: string;
    objectPosition?: string;
};

export type SplitContentTilesHeadingLevel = 2 | 3 | 4;

export type SplitContentTilesItem = {
    id?: string | number;
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    paragraphs?: React.ReactNode[];
    image?: SplitContentTilesImage;
    imageUrl?: string;
    imageAlt?: string;
    imagePosition?: string;
    accentColor?: string;
    backgroundColor?: string;
    textColor?: string;
    mutedTextColor?: string;
    reverse?: boolean;
};

export type SplitContentTilesProps = {
    items?: SplitContentTilesItem[];
    eyebrow?: React.ReactNode;
    title?: React.ReactNode;
    description?: React.ReactNode;
    paragraphs?: React.ReactNode[];
    image?: SplitContentTilesImage;
    imageUrl?: string;
    imageAlt?: string;
    imagePosition?: string;
    dark?: boolean;
    reverse?: boolean;
    accentColor?: string;
    backgroundColor?: string;
    tileBackgroundColor?: string;
    textColor?: string;
    mutedTextColor?: string;
    borderColor?: string;
    maxWidth?: number | string;
    imageAspectRatio?: string;
    headingLevel?: SplitContentTilesHeadingLevel;
    className?: string;
    ariaLabel?: string;
};

type SplitContentTilesStyle = React.CSSProperties & Record<`--${string}`, string | undefined>;

const splitContentTilesCss = `
.split-content-tiles {
  --split-content-tiles-bg: #050607;
  --split-content-tiles-tile-bg: #080a0b;
  --split-content-tiles-text: #ffffff;
  --split-content-tiles-muted: rgba(255, 255, 255, 0.74);
  --split-content-tiles-accent: #ed1c24;
  --split-content-tiles-border: rgba(255, 255, 255, 0.08);
  --split-content-tiles-max-width: 1440px;
  --split-content-tiles-image-ratio: 1.42 / 1;
  width: 100%;
  background: var(--split-content-tiles-bg);
  color: var(--split-content-tiles-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  box-sizing: border-box;
}
.split-content-tiles,
.split-content-tiles * {
  box-sizing: border-box;
}
.split-content-tiles-shell {
  width: 100%;
  max-width: var(--split-content-tiles-max-width);
  margin: 0 auto;
  padding: 50px 28px;
}
.split-content-tiles-stack {
  display: grid;
  gap: 28px;
}
.split-content-tiles-tile {
  --split-content-tiles-tile-accent: var(--split-content-tiles-accent);
  --split-content-tiles-tile-bg-local: var(--split-content-tiles-tile-bg);
  --split-content-tiles-tile-text-local: var(--split-content-tiles-text);
  --split-content-tiles-tile-muted-local: var(--split-content-tiles-muted);
  position: relative;
  display: grid;
  grid-template-columns: minmax(320px, 0.94fr) minmax(0, 1.06fr);
  align-items: center;
  gap: 56px;
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--split-content-tiles-border);
  border-radius: 8px;
  background: var(--split-content-tiles-tile-bg-local);
  color: var(--split-content-tiles-tile-text-local);
  padding: 28px;
}
.split-content-tiles-tile::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: var(--split-content-tiles-tile-accent);
  opacity: 0.86;
}
.split-content-tiles-tile--reverse .split-content-tiles-media {
  order: 2;
}
.split-content-tiles-tile--reverse .split-content-tiles-content {
  order: 1;
}
.split-content-tiles-tile--no-media {
  grid-template-columns: minmax(0, 1fr);
}
.split-content-tiles-media {
  min-width: 0;
}
.split-content-tiles-image-frame,
.split-content-tiles-image-fallback {
  width: 100%;
  aspect-ratio: var(--split-content-tiles-image-ratio);
  min-height: 330px;
}
.split-content-tiles-image-frame {
  overflow: hidden;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.06);
}
.split-content-tiles-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: var(--split-content-tiles-image-position, center);
}
.split-content-tiles-image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  border: 1px solid var(--split-content-tiles-border);
  border-radius: 4px;
  color: var(--split-content-tiles-tile-muted-local);
  font-size: 14px;
  line-height: 1.4;
  font-weight: 800;
  text-align: center;
  background: rgba(255, 255, 255, 0.06);
}
.split-content-tiles-content {
  min-width: 0;
  max-width: 790px;
  padding: 12px 10px 12px 0;
}
.split-content-tiles-eyebrow {
  margin: 0 0 24px;
  color: var(--split-content-tiles-tile-accent);
  font-size: 15px;
  line-height: 1.2;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}
.split-content-tiles-title {
  max-width: 100%;
  margin: 0;
  color: var(--split-content-tiles-tile-text-local);
  font-size: 48px;
  line-height: 1.16;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.split-content-tiles-body {
  max-width: 760px;
  margin-top: 30px;
  color: var(--split-content-tiles-tile-muted-local);
  font-size: 20px;
  line-height: 1.58;
  font-weight: 700;
  letter-spacing: 0;
}
.split-content-tiles-body p,
.split-content-tiles-paragraph {
  margin: 0;
}
.split-content-tiles-body p + p,
.split-content-tiles-paragraph + .split-content-tiles-paragraph {
  margin-top: 26px;
}
.split-content-tiles-light {
  --split-content-tiles-bg: #ffffff;
  --split-content-tiles-tile-bg: #f8fafc;
  --split-content-tiles-text: #111827;
  --split-content-tiles-muted: #4b5563;
  --split-content-tiles-border: rgba(17, 24, 39, 0.1);
}
.split-content-tiles-light .split-content-tiles-image-frame,
.split-content-tiles-light .split-content-tiles-image-fallback {
  background: #e5e7eb;
}
@media (max-width: 1100px) {
  .split-content-tiles-tile {
    grid-template-columns: minmax(280px, 0.92fr) minmax(0, 1.08fr);
    gap: 38px;
    padding: 24px;
  }
  .split-content-tiles-title {
    font-size: 40px;
  }
  .split-content-tiles-body {
    font-size: 18px;
  }
}
@media (max-width: 820px) {
  .split-content-tiles-shell {
    padding: 38px 18px;
  }
  .split-content-tiles-tile,
  .split-content-tiles-tile--reverse {
    grid-template-columns: minmax(0, 1fr);
    gap: 28px;
    padding: 18px;
  }
  .split-content-tiles-tile--reverse .split-content-tiles-media,
  .split-content-tiles-tile--reverse .split-content-tiles-content {
    order: initial;
  }
  .split-content-tiles-image-frame,
  .split-content-tiles-image-fallback {
    min-height: 250px;
  }
  .split-content-tiles-content {
    max-width: none;
    padding: 0 2px 8px;
  }
  .split-content-tiles-eyebrow {
    margin-bottom: 18px;
    font-size: 13px;
  }
  .split-content-tiles-title {
    font-size: 32px;
    line-height: 1.22;
  }
  .split-content-tiles-body {
    margin-top: 22px;
    font-size: 16px;
    line-height: 1.64;
  }
  .split-content-tiles-body p + p,
  .split-content-tiles-paragraph + .split-content-tiles-paragraph {
    margin-top: 20px;
  }
}
@media (max-width: 380px) {
  .split-content-tiles-shell {
    padding-right: 14px;
    padding-left: 14px;
  }
  .split-content-tiles-title {
    font-size: 28px;
  }
}
`;

function getCssSize(value: number | string): string {
    return typeof value === 'number' ? `${value}px` : value;
}

function getImageSrc(image?: SplitContentTilesImage, imageUrl?: string): string {
    return image?.thumbnail || image?.src || image?.url || image?.original || imageUrl || '';
}

function getImageAlt(image?: SplitContentTilesImage, imageAlt?: string): string {
    return (
        imageAlt || image?.alt || image?.thumbnailAlt || image?.originalAlt || image?.title || ''
    );
}

function getDirectTile({
    eyebrow,
    title,
    description,
    paragraphs,
    image,
    imageUrl,
    imageAlt,
    imagePosition,
    accentColor,
    textColor,
    mutedTextColor,
    reverse,
}: SplitContentTilesProps): SplitContentTilesItem | undefined {
    const hasDirectContent =
        !!eyebrow || !!title || !!description || !!paragraphs?.length || !!image || !!imageUrl;

    if (!hasDirectContent) return undefined;

    return {
        id: 'split-content-tile',
        eyebrow,
        title,
        description,
        paragraphs,
        image,
        imageUrl,
        imageAlt,
        imagePosition,
        accentColor,
        textColor,
        mutedTextColor,
        reverse,
    };
}

function renderHeading(title: React.ReactNode, headingLevel: SplitContentTilesHeadingLevel) {
    if (headingLevel === 4) return <h4 className="split-content-tiles-title">{title}</h4>;
    if (headingLevel === 3) return <h3 className="split-content-tiles-title">{title}</h3>;
    return <h2 className="split-content-tiles-title">{title}</h2>;
}

function renderBody(tile: SplitContentTilesItem) {
    if (tile.paragraphs?.length) {
        return (
            <div className="split-content-tiles-body">
                {tile.paragraphs.map((paragraph, index) => (
                    <p className="split-content-tiles-paragraph" key={index}>
                        {paragraph}
                    </p>
                ))}
            </div>
        );
    }

    if (!tile.description) return null;

    return (
        <div className="split-content-tiles-body">
            {typeof tile.description === 'string' ? <p>{tile.description}</p> : tile.description}
        </div>
    );
}

export default function SplitContentTiles({
    items,
    dark = true,
    reverse = false,
    accentColor = '#ed1c24',
    backgroundColor,
    tileBackgroundColor,
    textColor,
    mutedTextColor,
    borderColor,
    maxWidth = 1440,
    imageAspectRatio = '1.42 / 1',
    headingLevel = 2,
    className = '',
    ariaLabel,
    ...directTileProps
}: SplitContentTilesProps) {
    const directTile = getDirectTile({
        ...directTileProps,
        accentColor,
        textColor,
        mutedTextColor,
        reverse,
    });
    const resolvedItems = items?.length ? items : directTile ? [directTile] : [];

    if (!resolvedItems.length) return null;

    return (
        <section
            className={['split-content-tiles', dark ? '' : 'split-content-tiles-light', className]
                .filter(Boolean)
                .join(' ')}
            aria-label={ariaLabel}
            style={
                {
                    '--split-content-tiles-bg': backgroundColor,
                    '--split-content-tiles-tile-bg': tileBackgroundColor,
                    '--split-content-tiles-text': textColor,
                    '--split-content-tiles-muted': mutedTextColor,
                    '--split-content-tiles-accent': accentColor,
                    '--split-content-tiles-border': borderColor,
                    '--split-content-tiles-max-width': getCssSize(maxWidth),
                    '--split-content-tiles-image-ratio': imageAspectRatio,
                } as SplitContentTilesStyle
            }
        >
            <style>{splitContentTilesCss}</style>

            <div className="split-content-tiles-shell">
                <div className="split-content-tiles-stack">
                    {resolvedItems.map((tile, index) => {
                        const imageSrc = getImageSrc(tile.image, tile.imageUrl);
                        const imageAlt = getImageAlt(tile.image, tile.imageAlt);
                        const imagePosition =
                            tile.imagePosition || tile.image?.objectPosition || 'center';
                        const tileAccentColor = tile.accentColor || accentColor;
                        const tileHasCopy =
                            !!tile.eyebrow ||
                            !!tile.title ||
                            !!tile.description ||
                            !!tile.paragraphs?.length;
                        const tileIsReversed = tile.reverse ?? reverse;
                        const tileClassName = [
                            'split-content-tiles-tile',
                            tileIsReversed ? 'split-content-tiles-tile--reverse' : '',
                            !imageSrc && !tile.image ? 'split-content-tiles-tile--no-media' : '',
                        ]
                            .filter(Boolean)
                            .join(' ');
                        const tileStyle = {
                            '--split-content-tiles-tile-accent': tileAccentColor,
                            '--split-content-tiles-tile-bg-local': tile.backgroundColor,
                            '--split-content-tiles-tile-text-local': tile.textColor,
                            '--split-content-tiles-tile-muted-local': tile.mutedTextColor,
                            '--split-content-tiles-image-position': imagePosition,
                        } as SplitContentTilesStyle;

                        return (
                            <article
                                className={tileClassName}
                                key={tile.id ?? index}
                                style={tileStyle}
                            >
                                {imageSrc || tile.image ? (
                                    <div className="split-content-tiles-media">
                                        {imageSrc ? (
                                            <div className="split-content-tiles-image-frame">
                                                <img
                                                    src={imageSrc}
                                                    alt={imageAlt}
                                                    className="split-content-tiles-image"
                                                    loading={index === 0 ? 'eager' : 'lazy'}
                                                />
                                            </div>
                                        ) : (
                                            <div className="split-content-tiles-image-fallback">
                                                {tile.image?.title || 'Image'}
                                            </div>
                                        )}
                                    </div>
                                ) : null}

                                {tileHasCopy ? (
                                    <div className="split-content-tiles-content">
                                        {tile.eyebrow ? (
                                            <p className="split-content-tiles-eyebrow">
                                                {tile.eyebrow}
                                            </p>
                                        ) : null}
                                        {tile.title
                                            ? renderHeading(tile.title, headingLevel)
                                            : null}
                                        {renderBody(tile)}
                                    </div>
                                ) : null}
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
